import React, { useState, useEffect } from "react";
import SwoodleUpdatedLogo from "../../../Assets/Icons/SwoodleUpdatedLogo.svg";
import PrimaryButton from "../../common/PrimaryButton";
import TextInput from "../../common/SignUp/TextInput";
import emailIcon from "../../../Assets/Icons/emailIcon.svg";
import passwordIcon from "../../../Assets/Icons/passwordIcon.svg";
import {
  setLoginData,
  setUserData,
  clearAuth,
} from "../../../redux/slices/authSlice";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { loginSchema } from "../../../utils/validationSchemas";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { getOnboardingStatus } from "../../../services/fundraiserService";
// import { getUserSubscription } from "../../../services/subscriptionService";
import Checkbox from "../../common/Checkbox";
import { ReactComponent as UncheckedIcon } from "../../../Assets/Icons/uncheckedBlackBox.svg";
import Loader from "../../common/Loader";
import { getFcmToken } from "../../../utils/connectStore";
import axios from "../../../utils/axios";
import "../../common/OfferForm/offerform.scss"; // Import styles for the modal

const DISABLED_FUNDRAISER_MESSAGE =
  "Your account is disabled. Please contact support.";
const DELETED_FUNDRAISER_MESSAGE =
  "Your account have been deleted. Please Contact Support";
const PENDING_FUNDRAISER_MESSAGE =
  "Your account is pending admin approval. Please wait for approval.";
const DECLINED_FUNDRAISER_MESSAGE =
  "Your account has been declined. Please contact support.";
const REJECTED_FUNDRAISER_MESSAGE =
  "Your account has been rejected. Please contact support.";

const AccountLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberCheckbox, setRememberCheckbox] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState("");
  const [pendingMessage, setPendingMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showAccountPendingModal, setShowAccountPendingModal] = useState(false);
  const [accountBlockStatus, setAccountBlockStatus] = useState(null);
  const loading = useSelector((state) => state?.onboardingReducer?.loading);
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const user = useSelector((state) => state?.authReducer?.tokenData?.user);

  const clearFundraiserSessionState = () => {
    sessionStorage.removeItem("showAccountPendingModal");
    sessionStorage.removeItem("fundraiser_stripe_complete");
    sessionStorage.removeItem("fundraiser_block_status");
    sessionStorage.removeItem("fundraiser_verified");
    sessionStorage.removeItem("skip_auto_redirect");
  };

  const normalizeAuthErrorMessage = (message) => {
    const normalizedMessage =
      typeof message === "string" ? message.trim().toLowerCase() : "";

    if (
      normalizedMessage.includes("fundraiser account has been disabled") ||
      normalizedMessage === "your account has been disabled"
    ) {
      return DISABLED_FUNDRAISER_MESSAGE;
    }

    if (
      normalizedMessage.includes("fundraiser account has been deleted") ||
      normalizedMessage === "your account has been deleted"
    ) {
      return DELETED_FUNDRAISER_MESSAGE;
    }

    if (normalizedMessage === "your account has been rejected") {
      return REJECTED_FUNDRAISER_MESSAGE;
    }

    if (normalizedMessage === "your account has been declined") {
      return DECLINED_FUNDRAISER_MESSAGE;
    }

    return message || "Invalid email or password";
  };

  // Check for pending approval message from navigation state or success message from Stripe completion
  useEffect(() => {
    // CRITICAL: Check flags FIRST before removing them to prevent race condition
    const stripeJustCompleted =
      sessionStorage.getItem("showAccountPendingModal") === "true";
    const stripeCompleteFlag =
      sessionStorage.getItem("fundraiser_stripe_complete") === "true";
    const blockedStatusFromSession = sessionStorage.getItem(
      "fundraiser_block_status",
    );
    const normalizedBlockedStatusFromSession = String(
      blockedStatusFromSession || "",
    ).toLowerCase();
    const isInlineBlockedStatus =
      normalizedBlockedStatusFromSession === "disabled" ||
      normalizedBlockedStatusFromSession === "deleted";
    const locationMessage = String(location.state?.message || "");
    const normalizedLocationMessage = locationMessage.toLowerCase();
    const hasInlineBlockedLocationMessage =
      normalizedLocationMessage.includes("disabled") ||
      normalizedLocationMessage.includes("deleted");

    let shouldShowModal = false;
    let preventAutoRedirect = false;

    // Check for success message from Stripe completion
    if (stripeCompleteFlag) {
      setSuccessMessage(
        "Your Stripe account setup is complete! Your account is now pending approval.",
      );
      sessionStorage.removeItem("fundraiser_stripe_complete");
      preventAutoRedirect = true; // Prevent auto-redirect to let user see message
    }

    // Check for account pending modal flag in sessionStorage
    if (stripeJustCompleted && !isInlineBlockedStatus) {
      if (
        normalizedBlockedStatusFromSession === "rejected" ||
        normalizedBlockedStatusFromSession === "declined"
      ) {
        shouldShowModal = true;
      }
      sessionStorage.removeItem("showAccountPendingModal");
      preventAutoRedirect = true; // Prevent auto-redirect to let modal show
    }

    // Blocked accounts can come from navigation/guard
    if (blockedStatusFromSession) {
      const normalizedBlockedStatus = normalizedBlockedStatusFromSession;
      preventAutoRedirect = true;
      if (normalizedBlockedStatus === "disabled") {
        setAuthErrorMessage(DISABLED_FUNDRAISER_MESSAGE);
        setShowAccountPendingModal(false);
        setAccountBlockStatus(null);
        setPendingMessage("");
      } else if (normalizedBlockedStatus === "deleted") {
        setAuthErrorMessage(DELETED_FUNDRAISER_MESSAGE);
        setShowAccountPendingModal(false);
        setAccountBlockStatus(null);
        setPendingMessage("");
      } else if (
        normalizedBlockedStatus === "pending" ||
        normalizedBlockedStatus === "in_progress"
      ) {
        showInlinePendingApproval();
      } else {
        setAccountBlockStatus(normalizedBlockedStatus);
        shouldShowModal = true;
      }
      sessionStorage.removeItem("showAccountPendingModal");
      sessionStorage.removeItem("fundraiser_block_status");
    }

    // Check for account pending modal flag in location state
    if (
      location.state?.showAccountPendingModal === true &&
      !isInlineBlockedStatus &&
      !hasInlineBlockedLocationMessage
    ) {
      if (
        normalizedLocationMessage.includes("rejected") ||
        normalizedLocationMessage.includes("declined")
      ) {
        shouldShowModal = true;
      } else if (normalizedLocationMessage.includes("pending")) {
        showInlinePendingApproval(
          locationMessage || PENDING_FUNDRAISER_MESSAGE,
        );
      }
      preventAutoRedirect = true;
      window.history.replaceState({}, document.title);
    }

    // Set the modal state if needed
    if (shouldShowModal) {
      setShowAccountPendingModal(true);
    }

    // Handle message from location state
    if (location.state?.message) {
      const message = location.state.message;
      const msgLower = String(message).toLowerCase();

      if (msgLower.includes("deleted")) {
        setAuthErrorMessage(DELETED_FUNDRAISER_MESSAGE);
        setShowAccountPendingModal(false);
        setAccountBlockStatus(null);
        setPendingMessage("");
        preventAutoRedirect = true;
      } else if (msgLower.includes("disabled")) {
        setAuthErrorMessage(DISABLED_FUNDRAISER_MESSAGE);
        setShowAccountPendingModal(false);
        setAccountBlockStatus(null);
        setPendingMessage("");
        preventAutoRedirect = true;
      } else if (msgLower.includes("rejected")) {
        setAccountBlockStatus("rejected");
        setPendingMessage(message);
        shouldShowModal = true;
        setShowAccountPendingModal(true);
        preventAutoRedirect = true;
      } else if (msgLower.includes("declined")) {
        setAccountBlockStatus("declined");
        setPendingMessage(message);
        shouldShowModal = true;
        setShowAccountPendingModal(true);
        preventAutoRedirect = true;
      } else if (msgLower.includes("pending")) {
        showInlinePendingApproval(message);
        preventAutoRedirect = true;
      }
      // Clean up state after reading
      window.history.replaceState({}, document.title);
    }

    // ONLY run auto-redirect if we're NOT showing a modal
    const alreadyAuthenticated =
      token && user?.role?.toLowerCase() === "fundraiser";
    if (
      alreadyAuthenticated &&
      !location.state?.skipAutoRedirect &&
      sessionStorage.getItem("skip_auto_redirect") !== "true" &&
      !preventAutoRedirect // Don't auto-redirect if modal needs to show
    ) {
      const autoRedirect = async () => {
        try {
          const statusPromise = await dispatch(getOnboardingStatus()).unwrap();
          const detailsSubmitted =
            statusPromise?.data?.detailsSubmitted === true;
          const chargesEnabled = statusPromise?.data?.chargesEnabled === true;

          if (!detailsSubmitted || !chargesEnabled) {
            if (detailsSubmitted && !chargesEnabled) {
              navigate("/complete-setup", { replace: true });
            } else {
              navigate("/fundraiser/welcome", { replace: true });
            }
            return;
          }
        } catch (error) {
          navigate("/fundraiser/welcome", { replace: true });
          return;
        }

        let userStatus =
          typeof user?.status === "string"
            ? user.status.toLowerCase()
            : user?.status;
        try {
          const profileResponse = await axios.get("fundraiser/profile", {
            headers: { "Cache-Control": "no-cache" },
            params: { _: Date.now() },
          });
          const profileStatusRaw = profileResponse?.data?.data?.status;
          userStatus =
            typeof profileStatusRaw === "string"
              ? profileStatusRaw.toLowerCase()
              : profileStatusRaw;
        } catch (profileError) {
          // Fall back to auth state if profile lookup fails here.
        }
        const isApproved = userStatus === "approved" || userStatus === "live";

        if (isApproved) {
          navigate("/fundraiser/dashboard", { replace: true });
        } else {
          // For non-approved users, clear auth but don't redirect immediately
          // Let them see the login page with the correct blocked-state UX
          dispatch(clearAuth());
          if (userStatus === "deleted") {
            setAuthErrorMessage(DELETED_FUNDRAISER_MESSAGE);
            setShowAccountPendingModal(false);
            setAccountBlockStatus(null);
            setPendingMessage("");
          } else if (userStatus === "disabled") {
            setAuthErrorMessage(DISABLED_FUNDRAISER_MESSAGE);
            setShowAccountPendingModal(false);
            setAccountBlockStatus(null);
            setPendingMessage("");
          } else if (userStatus === "rejected" || userStatus === "declined") {
            setAccountBlockStatus(userStatus);
            setShowAccountPendingModal(true);
          } else {
            showInlinePendingApproval();
          }
        }
      };

      autoRedirect();
    }
  }, [location, token, user, navigate, dispatch]);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      // Clear any previous error messages on new submission
      clearFundraiserSessionState();
      dispatch(clearAuth());
      setAuthErrorMessage("");
      setPendingMessage("");
      setSuccessMessage("");
      setShowAccountPendingModal(false);
      setAccountBlockStatus(null);

      try {
        const promiseResult = await axios
          .post("auth/login", {
            email: values.email,
            password: values.password,
            role: "fundraiser",
            fcmToken: getFcmToken(),
          })
          .then((response) => response?.data);

        if (promiseResult?.success !== true) {
          setAuthErrorMessage("Invalid email or password");
          formik.setTouched({ email: true, password: true });
          formik.setFieldError("email", "Invalid email or password");
          formik.setFieldError("password", "Invalid email or password");
          // Clear auth in case token was set
          dispatch(clearAuth());
          return;
        }

        const user = promiseResult?.data?.user || promiseResult?.user;
        const tempToken = promiseResult?.data?.token;
        const authHeaders = tempToken
          ? {
              Authorization: `Bearer ${tempToken}`,
              "api-key": process.env.REACT_APP_API_KEY,
            }
          : null;

        const persistAuthData = async () => {
          if (!promiseResult?.data?.token) {
            throw new Error("No token received from login");
          }

          dispatch(setUserData(promiseResult.data));

          try {
            const existingState = JSON.parse(
              localStorage.getItem("persist:root") || "{}",
            );
            const authState =
              typeof existingState.authReducer === "string"
                ? JSON.parse(existingState.authReducer)
                : existingState.authReducer;

            const updatedAuthState = {
              ...authState,
              tokenData: promiseResult.data,
            };

            localStorage.setItem(
              "persist:root",
              JSON.stringify({
                ...existingState,
                authReducer: JSON.stringify(updatedAuthState),
              }),
            );
          } catch (_) {}

          await new Promise((resolve) => setTimeout(resolve, 100));

          dispatch(
            setLoginData({
              email: values.email,
              password: values.password,
              fcmToken: getFcmToken(),
            }),
          );
        };

        // Normalize role and status for robustness (e.g., "FUNDRAISER", "APPROVED")
        const userRoleRaw = user?.role;
        const userRole =
          typeof userRoleRaw === "string"
            ? userRoleRaw.toLowerCase()
            : userRoleRaw;

        // Block User/Member accounts
        if (userRole === "user" || userRole === "member") {
          setAuthErrorMessage(
            "This email is registered as a User/Member account. User/Member accounts cannot log in here. Please use the mobile app.",
          );
          formik.setTouched({ email: true, password: true });
          formik.setFieldError(
            "email",
            "This email is registered as a User/Member account. User/Member accounts cannot log in here. Please use the mobile app.",
          );
          formik.setFieldError(
            "password",
            "This email is registered as a User/Member account. User/Member accounts cannot log in here. Please use the mobile app.",
          );
          dispatch(clearAuth());
          return;
        }

        if (userRole && userRole !== "fundraiser") {
          setAuthErrorMessage("Invalid email or password");
          formik.setTouched({ email: true, password: true });
          formik.setFieldError("email", "Invalid email or password");
          formik.setFieldError("password", "Invalid email or password");
          // Clear auth in case token was set
          dispatch(clearAuth());
          return;
        }

        // Check approval status first so blocked accounts never enter dashboard/welcome flows.
        let fundraiserProfile;
        try {
          if (!authHeaders) {
            throw new Error("Missing auth token");
          }
          const profileResponse = await axios.get("fundraiser/profile", {
            headers: authHeaders,
            params: { _: Date.now() },
          });
          fundraiserProfile = profileResponse?.data?.data;
        } catch (profileError) {
          console.error("Failed to fetch fundraiser profile:", profileError);
          dispatch(clearAuth());
          setAuthErrorMessage(
            "Failed to verify account status. Please try again.",
          );
          formik.setTouched({ email: true, password: true });
          formik.setFieldError(
            "email",
            "Failed to verify account status. Please try again.",
          );
          formik.setFieldError(
            "password",
            "Failed to verify account status. Please try again.",
          );
          return;
        }

        // Check admin approval status from the profile
        const profileStatus =
          typeof fundraiserProfile?.status === "string"
            ? fundraiserProfile.status.toLowerCase()
            : fundraiserProfile?.status;

        const statusLower = String(profileStatus || "").toLowerCase();

        const declinedOrRejectedMsg =
          statusLower === "rejected"
            ? "Your account has been rejected. Please contact support."
            : "Your account has been declined. Please contact support.";

        const disabledMsg = DISABLED_FUNDRAISER_MESSAGE;

        const isApproved = statusLower === "approved" || statusLower === "live";

        if (statusLower === "deleted") {
          dispatch(clearAuth());
          clearFundraiserSessionState();
          setShowAccountPendingModal(false);
          setAccountBlockStatus(null);
          setPendingMessage("");
          setAuthErrorMessage(DELETED_FUNDRAISER_MESSAGE);
          formik.setTouched({ email: true, password: true });
          formik.setFieldError("email", DELETED_FUNDRAISER_MESSAGE);
          formik.setFieldError("password", DELETED_FUNDRAISER_MESSAGE);
          return;
        }

        if (statusLower === "declined" || statusLower === "rejected") {
          dispatch(clearAuth());
          clearFundraiserSessionState();
          setAccountBlockStatus(statusLower);
          setAuthErrorMessage(declinedOrRejectedMsg);
          setPendingMessage(declinedOrRejectedMsg);
          setShowAccountPendingModal(true);
          formik.setTouched({ email: true, password: true });
          formik.setFieldError("email", declinedOrRejectedMsg);
          formik.setFieldError("password", declinedOrRejectedMsg);
          return;
        }

        if (statusLower === "disabled") {
          dispatch(clearAuth());
          clearFundraiserSessionState();
          setShowAccountPendingModal(false);
          setAccountBlockStatus(null);
          setPendingMessage("");
          setAuthErrorMessage(disabledMsg);
          formik.setTouched({ email: true, password: true });
          formik.setFieldError("email", disabledMsg);
          formik.setFieldError("password", disabledMsg);
          return;
        }

        if (!isApproved) {
          dispatch(clearAuth());
          clearFundraiserSessionState();
          showInlinePendingApproval();
          return;
        }

        // Approved accounts can continue to Stripe onboarding checks.
        const fetchStripeStatus = async () => {
          if (!authHeaders) {
            throw new Error("Missing auth token");
          }
          const statusResponse = await axios.get("payout/onboardingStatus", {
            headers: authHeaders,
          });
          return statusResponse?.data?.data;
        };

        try {
          const stripeStatus = await fetchStripeStatus();
          const detailsSubmitted = stripeStatus?.detailsSubmitted === true;
          const chargesEnabled = stripeStatus?.chargesEnabled === true;

          if (!detailsSubmitted || !chargesEnabled) {
            clearFundraiserSessionState();
            await persistAuthData();

            if (detailsSubmitted && !chargesEnabled) {
              navigate("/complete-setup", { replace: true });
            } else {
              navigate("/fundraiser/welcome", { replace: true });
            }
            return;
          }
        } catch (error) {
          clearFundraiserSessionState();
          await persistAuthData();
          navigate("/fundraiser/welcome", { replace: true });
          return;
        }

        sessionStorage.setItem("fundraiser_verified", "true");
        await persistAuthData();
        navigate("/fundraiser/dashboard", { replace: true });
      } catch (err) {
        clearFundraiserSessionState();
        const errorMessage = normalizeAuthErrorMessage(
          err?.response?.data?.message || err?.message,
        );
        setAuthErrorMessage(errorMessage);
        formik.setTouched({ email: true, password: true });
        formik.setFieldError("email", errorMessage);
        formik.setFieldError("password", errorMessage);
        // Clear auth in case token was set
        dispatch(clearAuth());
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { email, password } = formik.values;

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
    isSubmitting,
  } = formik;

  const showInlinePendingApproval = (message = PENDING_FUNDRAISER_MESSAGE) => {
    setAuthErrorMessage(message);
    setPendingMessage(message);
    setAccountBlockStatus("pending");
    setShowAccountPendingModal(false);
    formik.setTouched({ password: true }, false);
    formik.setFieldError("password", message);
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSignupClick = () => {
    navigate("/fundraiser/getting-started");
  };

  const handleCloseAccountPendingModal = () => {
    setShowAccountPendingModal(false);
    setAccountBlockStatus(null);
    // Clear sessionStorage flags to prevent modal from reappearing
    clearFundraiserSessionState();
    // Also clear the location state to prevent re-showing
    if (location.state?.showAccountPendingModal) {
      window.history.replaceState({}, document.title);
    }
  };

  return (
    <form
      className="w-full py-10 h-screen flex flex-col items-center offerform"
      onSubmit={handleSubmit}
    >
      {loading && <Loader />}
      <p className="font-poppins font-[500] text-[14px] sm:text-[18px] md:text-[22px] lg:text-[26px] text-[#303030] px-4">
        Welcome to Swoodle! Log into your Account!
      </p>

      <div className="flex flex-col items-center w-[90%] sm:w-[70%] md:w-[60%] lg:w-[50%] xl:w-[45%] 2xl:w-[40%]">
        <img
          src={SwoodleUpdatedLogo}
          alt="Swoodle"
          className="pt-[6%] pb-[2%] w-[60%]"
        ></img>
        <p className="font-poppins font-[500] sm:text-[18px] md:text-[22px] lg:text-[26px] text-[#303030]">
          FUNDRAISER
        </p>
        <p className="font-poppins font-[400] text-[14px] sm:text-[16px] md:text-[18px] lg:text-[18px] xl:text-[22px] text-[#303030] mt-6 sm:mt-10 mb-3 text-center">
          Your Account
        </p>
        {successMessage && (
          <div className="w-full bg-green-50 border border-green-200 rounded-md p-3 mb-3">
            <p className="text-[12px] sm:text-[14px] text-green-800 font-poppins text-center">
              {successMessage}
            </p>
          </div>
        )}
        {/* Removed top-level auth error banner; inline field errors remain */}
        <div className="flex flex-col justify-start items-start gap-2 w-full">
          <TextInput
            Icon={emailIcon}
            name={"email"}
            value={email}
            placeholder={"Email ID"}
            inputClass={
              "w-full rounded-tr-[5px] rounded-br-[5px] border border-[#D9D9D9] pl-[3%]"
            }
            iconAreaClass={
              "w-[11%] bg-[#E6E6E6] rounded-tl-[5px] rounded-bl-[5px] border border-[#D9D9D9]"
            }
            innerDivClass={"h-[45px] sm:h-[45px] md:h-[50px]"}
            inputDivClass={"h-[70px] sm:h-[65px]"}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={errors.email}
            touched={touched.email}
            autoComplete={"new-password"}
          />

          <TextInput
            Icon={passwordIcon}
            type={showPassword ? "text" : "password"}
            name={"password"}
            value={password}
            placeholder={"Password"}
            inputClass={
              "w-full rounded-tr-[5px] rounded-br-[5px] border border-[#D9D9D9] pl-[3%]"
            }
            iconAreaClass={
              "w-[11%] bg-[#E6E6E6] rounded-tl-[5px] rounded-bl-[5px] border border-[#D9D9D9]"
            }
            innerDivClass={"h-[45px] sm:h-[45px] md:h-[50px]"}
            inputDivClass={"h-[70px] sm:h-[65px]"}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={errors.password || authErrorMessage}
            touched={touched.password}
            showPassword={showPassword}
            togglePassword={togglePassword}
            autoComplete={"new-password"}
          />
          <div className="w-full flex items-center justify-between mt-1 sm:mt-1 2xl:mt-[0%]">
            <div className=" w-[60%] flex items-center customize">
              <Checkbox
                text="Remember me"
                divClassName="flex items-center justify-center"
                checked={rememberCheckbox}
                onChecked={() => {
                  setRememberCheckbox(!rememberCheckbox);
                }}
                textClass="text-[12px] sm:text-[12px] lg:text-[14px] font-[400] font-poppins text-[#767676] ml-2 whitespace-nowrap"
                checkClassName="!w-[18px] !h-[18px] sm:!w-[20px] sm:!h-[20px] -translate-y-[2px]"
              />
            </div>

            <div className="w-[50%] text-right">
              <p className="text-[10px] sm:text-[12px] lg:text-[14px] font-[400] text-[#767676] font-poppins">
                <NavLink to="/forget-password">Forget Password</NavLink>
              </p>
            </div>
          </div>
        </div>
        <p className="text-[9px] sm:text-[12px] md:text-[14px] text-center font-poppins font-[400] mt-2 w-[95%]">
          <button
            type="button"
            className="text-[#00ADEF] font-[500] cursor-pointer hover:underline"
            onClick={() => {
              const mailto = "mailto:support@swoodle.com.au";
              try {
                const newWin = window.open(mailto, "_blank");
                if (!newWin) {
                  // Fallback: programmatically click a temporary anchor
                  const a = document.createElement("a");
                  a.href = mailto;
                  a.target = "_blank";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }
              } catch (_) {
                window.location.href = mailto;
              }
            }}
          >
            Need Help?
          </button>
        </p>
        <p className="text-[9px] sm:text-[12px] md:text-[14px] text-center font-poppins font-[400] mt-2 w-[95%]">
          Don't have a Fundraiser account?
          <span
            className="text-[#00ADEF] font-[500] cursor-pointer"
            onClick={handleSignupClick}
          >
            &nbsp; Sign up with Swoodle
          </span>
        </p>
        <div className="text-center w-full mt-4 sm:mt-5">
          <PrimaryButton
            type={"submit"}
            loading={isSubmitting}
            className={
              "w-full font-poppins text-[12px] sm:text-[14px] lg:text-[16px] text-center font-[500] h-[40px] sm:h-[45px] md:h-[50px] lg:h-[55px] rounded-[10px] mb-4 sm:mb-5 tracking-wide"
            }
            label="LOG INTO SWOODLE FUNDRAISER CENTRE"
          />
        </div>
      </div>

      {/* Account Status Modal - Kept for rejected/declined states */}
      {showAccountPendingModal &&
        !authErrorMessage &&
        ["rejected", "declined"].includes(
          String(accountBlockStatus || "").toLowerCase(),
        ) && (
          <div
            className="offer-success-modal-backdrop"
            onClick={handleCloseAccountPendingModal}
          >
            <div
              className="offer-success-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>
                {accountBlockStatus === "disabled"
                  ? "Account Disabled"
                  : accountBlockStatus === "rejected"
                    ? "Account Rejected"
                    : accountBlockStatus === "declined"
                      ? "Account Declined"
                      : "Account Pending"}
              </h3>
              <p className="offer-success-message">
                {accountBlockStatus === "disabled"
                  ? "Your account is disabled. Please contact support."
                  : accountBlockStatus === "rejected"
                    ? "Your account has been rejected. Please contact support."
                    : accountBlockStatus === "declined"
                      ? "Your account has been declined. Please contact support."
                      : "Your approval has been sent to the Swoodle Admin. Once the Swoodle Admin approves your account, you will receive an email notification."}
              </p>
              <div className="offer-success-actions">
                <PrimaryButton
                  className="offer-success-ok-btn w-[60%] h-[45px] rounded-[8px] text-[16px] font-[500] font-montserrat"
                  label="OK"
                  onClick={handleCloseAccountPendingModal}
                />
              </div>
            </div>
          </div>
        )}
    </form>
  );
};

export default AccountLogin;
